import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { formatDate, useResource } from '@landsbankinn/l16n';
import { Button, theme, useMedia } from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import Link from 'next/link';
import React, { useContext } from 'react';
import { ExchangeSymbol } from '../../../../../__generated__/globalTypes';
import type {
  fetchStocks,
  fetchStocksVariables,
  fetchStocks_stocks_nodes,
} from '../../../../graphql/__generated__/fetchStocks';
import { FETCH_STOCKS } from '../../../../graphql/fetchStocks';
import { DataTable } from '../../../table/DataTable';
import {
  MarketsBodyBold,
  MarketsNumberBold,
  MarketsNumberSmall,
  MarketsSmall,
  RightAlignColumnContainer,
  StickyFirstColumn,
  TwoNumberContainer,
} from '../../shared/MarketsComponents';
import {
  MarketsFormatNumber,
  NumberType,
} from '../../shared/MarketsFormatNumber';
import res from './../Stocks.res';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface IProps {
  stocks?: fetchStocks;
  exchange?: ExchangeSymbol;
  stockDetailPrefix: string;
  stockBuyUrl: (symbol: string) => string;
}

export function StocksMarketTable({
  stocks,
  exchange,
  stockDetailPrefix,
  stockBuyUrl,
}: IProps) {
  const { r, locale } = useResource();
  const isMobile = useMedia('sm');
  const { client } = useContext(LandsbankinnContext);

  const { data = stocks } = useQuery<fetchStocks, fetchStocksVariables>(
    FETCH_STOCKS,
    {
      client,
      variables: { exchange: exchange, first: 100 },
    },
  );

  const columns = React.useMemo(
    () => [
      {
        Header: r(res.company),
        accessor: 'symbol', // accessor is the "key" in the data
        headerStyle: {
          flexDirection: 'row-reverse',
        },
        Cell: ({ value, row: { original: stock } }: any) => (
          <>
            <TwoNumberContainer>
              <MarketsBodyBold>{value}</MarketsBodyBold>
              {!isMobile && <MarketsSmall noWrap>{stock.name}</MarketsSmall>}
            </TwoNumberContainer>
          </>
        ),
      },
      {
        Header: r(res.price),
        accessor: 'last',
        Cell: ({ value }: any) => (
          <MarketsNumberBold
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.change),
        sortType: 'basic',
        accessor: 'intradayPerChange',
        Cell: ({ value, row: { original: stock } }: any) => (
          <RightAlignColumnContainer>
            <TwoNumberContainer>
              <MarketsNumberSmall
                type={NumberType.Number}
                decimalScale={2}
                displayPosNegColor
                hideNullIndicator
                number={stock.lastPriceDifference}
              />
              <MarketsNumberSmall
                type={NumberType.Percentage}
                decimalScale={2}
                displayPosNegColor
                hideNullIndicator
                number={value}
              />
            </TwoNumberContainer>
          </RightAlignColumnContainer>
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.bid),
        accessor: 'bid',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.ask),
        accessor: 'ask',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.turnover),
        accessor: 'intradayAccumulatedTurnover',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value / 1000}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.numberOfTrades),
        accessor: 'intradayNumberOfTrades',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={0}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.time),
        accessor: 'lastTradeDate',
        Cell: ({ value }: any) =>
          value !== null && isToday(new Date(value))
            ? format(new Date(value), 'HH:mm')
            : formatDate(locale, new Date(value)),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.Highest),
        accessor: 'intradayHighPrice',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: r(res.Lowest),
        accessor: 'intradayLowPrice',
        Cell: ({ value }: any) => (
          <MarketsFormatNumber
            type={NumberType.Number}
            decimalScale={2}
            number={value}
          />
        ),
        style: {
          textAlign: 'right',
        },
      },
      {
        Header: '',
        accessor: 'exchangeSymbol',
        Cell: ({ value, row: { original: stock } }: any) => (
          <ButtonContainer>
            {value === ExchangeSymbol.XICE && (
              <Link href={stockBuyUrl(stock.symbol)} passHref>
                <Button
                  as="a"
                  title={r(res.buy)}
                  isSmall
                  bg={theme.colors.primary}
                  onClick={(e) => e.stopPropagation()}
                />
              </Link>
            )}
          </ButtonContainer>
        ),
      },
    ],
    [isMobile, locale, r],
  );

  const tableData = React.useMemo(() => {
    return data?.stocks.nodes;
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <StickyFirstColumn>
      <DataTable<fetchStocks_stocks_nodes>
        aria-label={r(res.stocks)}
        data={tableData as any}
        columns={columns as any}
        colorTheme="ternary"
        initialState={{
          sortBy: [
            {
              id: 'symbol',
              desc: false,
            },
          ],
        }}
        rowLink={({ original: stock }) => ({
          href: `${stockDetailPrefix}/[symbol]`,
          as: `${stockDetailPrefix}/${stock.symbol
            .split(' ')
            .join('_')
            .toLowerCase()}`,
        })}
        isClickable
      />
    </StickyFirstColumn>
  );
}
